/** @jsxImportSource @emotion/react */

import { CalciteChip } from "@esri/calcite-components-react";
import VersionNotesButton from "components/VersionNotesButton";
import { UserContext } from "context/UserContext";
import { useContext, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Criteria } from "services/APIBase.d";
import RecordService from "services/RecordService";
import tw from "twin.macro";
import Logo from "../Logo/Logo";

const useNavigationData = (user) => {
  const scope = user && user.functions;

  const navItems = [
    {
      name: "Dashboard",
      to: "/dashboard",
      scope: "foresite:dashboard",
    },
    {
      name: "ALI Records",
      to: "/ali-records",
      scope: "foresite:ali:view",
    },
    {
      name: "ALI Errors",
      to: "/ali-errors",
      scope: "foresite:ali_error:view",
    },
    {
      name: "MSAG Records",
      to: "/msag-records",
      scope: "foresite:msag:view",
    },
    {
      name: "ELT Records",
      to: "/elt-records",
      scope: "foresite:ELT:view",
    },
    {
      name: "Discrepancies",
      to: "/discrepancies",
      scope: [
        "foresite:discrep:updateByCarrier",
        "foresite:discrep:updateByJurisdiction",
      ],
    },
  ];

  return navItems.filter((item) => {
    if (Array.isArray(item.scope)) {
      return item.scope.some((s) => scope?.includes(s));
    }
    return scope?.includes(item.scope);
  });
};

const recordService = new RecordService("discrep");
export const fetchDiscrepancyCount = (user): Promise<number> => {
  return new Promise(async (resolve) => {
    let criterias: Criteria[] = [];

    if (user?.functions?.includes("foresite:discrep:updateByJurisdiction")) {
      // Select count(*) FROM discrep where (status <> 'Completed') AND ((<account privileges>))  AND   (status= 'Unvetted'  OR  status= 'Verified'  OR  question);
      criterias = [
        {
          field: "status",
          type: "OR",
          val_array: ["Unvetted", "Verified"],
          operator: "OR",
        },
        { field: "question", type: "=", value: "1" },
      ];
    } else if (user?.functions?.includes("foresite:discrep:updateByCarrier")) {
      // Select count(*) FROM discrep where (status <> 'Completed') AND ((<account privileges>))  AND   (status= 'New'  OR  status= 'Notified' ) AND question<>1;
      criterias = [
        {
          field: "status",
          type: "OR",
          val_array: ["New", "Notified"],
        },
        { field: "question", type: "=", value: "0" },
      ];
    }

    if (criterias.length === 0) {
      resolve(0);
      return;
    }

    try {
      const res = await recordService.getRecords(
        {
          criterias: criterias,
        },
        "count"
      );
      if (res.status === 200) {
        resolve(res.data.message.TotalRecordCount);
      } else {
        resolve(0);
      }
    } catch (error) {
      resolve(0);
    }
  });
};

const SideNav: React.FC = () => {
  const { user, hideNav, setHideNav, discrepancyCount, setDiscrepancyCount } =
    useContext(UserContext);
  const [isSmall, setIsSmall] = useState<boolean>(false);

  useEffect(() => {
    if (window.innerWidth <= 1100) {
      setHideNav(true);
      setIsSmall(true);
    }

    const handleResize = () => {
      if (window.innerWidth <= 1100) {
        setHideNav(true);
        setIsSmall(true);
      } else {
        setHideNav(false);
        setIsSmall(false);
      }
    };
    window.addEventListener("resize", handleResize);

    const updateDiscrepancyCount = async () => {
      if (user) {
        const count = await fetchDiscrepancyCount(user);
        setDiscrepancyCount(count);
      }
    };
    updateDiscrepancyCount();
    const interval = setInterval(() => updateDiscrepancyCount(), 30000);
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, [setHideNav]);

  const navigation = useNavigationData(user);

  return (
    <>
      <div
        css={[
          tw`md:translate-x-0 sm:translate-y-0 lg:h-screen bottom-0 lg:top-0 top-16 flex flex-col content-between pt-2 pl-4 pr-2 pb-2 border-r border-t border-gray-300 bg-white fixed lg:sticky w-full sm:w-2/6 max-w-sm lg:w-auto transition duration-300 ease-in-out z-20 sm:z-40`,
          hideNav &&
            tw`sm:-translate-x-full sm:translate-y-0 -translate-y-full transition duration-300 ease-in-out`,
        ]}
      >
        <div
          css={[tw`w-full px-8 py-4 m-auto hidden lg:block cursor-pointer`]}
          onClick={() => {
            // go to the first navigation link
            if (navigation && navigation.length > 0) {
              window.location.href = navigation[0].to;
            }
          }}
        >
          <Logo withText={true} />
        </div>
        <div css={[tw`flex-1 pt-4 md:pt-0 `]}>
          <div css={[tw`space-y-2 flex flex-col`]}>
            {navigation &&
              navigation.map((item, index) => (
                <NavLink
                  className={({ isActive }) =>
                    ["nav-link ", isActive ? "active" : ""].join(" ")
                  }
                  onClick={() => (isSmall ? setHideNav(true) : null)}
                  key={index}
                  to={item.to}
                >
                  {item.name}
                  {item.name === "Discrepancies" && (
                    <CalciteChip kind="brand" scale="s" value="1">
                      {discrepancyCount && discrepancyCount}
                    </CalciteChip>
                  )}
                </NavLink>
              ))}
          </div>
        </div>
        <VersionNotesButton />
      </div>
    </>
  );
};

export default SideNav;
